import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "dialog", "form", "status", "submit", "board", "totalCount", "toast",
    "catalogQuery", "catalogSelect", "catalogStatus", "catalogMore", "progress"
  ];

  connect() {
    this.connected = true;
    this.connectionGeneration = (this.connectionGeneration || 0) + 1;
    this.submitGeneration = (this.submitGeneration || 0) + 1;
    this.statusGeneration = (this.statusGeneration || 0) + 1;
    this.opener = null;
    this.closeTimer = null;
    this.toastTimer = null;
    this.pollTimer = null;
    this.catalogTimer = null;
    this.catalogAbort = null;
    this.submitAbort = null;
    this.statusAbort = null;
    this.catalogCursor = null;
    this.statusUrl = null;
    this.submitting = false;
  }

  disconnect() {
    this.connected = false;
    this.connectionGeneration += 1;
    this.submitGeneration += 1;
    this.statusGeneration += 1;
    this.clearTimers();
  }

  open(event) {
    event.preventDefault();
    if (!this.hasDialogTarget) return;

    this.opener = event.currentTarget;
    if (!this.statusUrl) this.resetStatus();
    if (!this.dialogTarget.open) this.dialogTarget.showModal();
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open && !this.submitting) {
      this.dialogTarget.close();
    }
  }

  closeFromBackdrop(event) {
    if (event.target === this.dialogTarget && !this.submitting) this.dialogTarget.close();
  }

  closed() {
    this.opener?.focus();
    this.opener = null;
  }

  async submit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (this.submitting || !(form instanceof HTMLFormElement)) return;

    this.stopStatusPolling();
    this.setSubmitting(true);
    this.updateProgress("submitted");
    this.setStatus("Sending to the priority request lane…");
    this.submitAbort?.abort();
    const abortController = new AbortController();
    const connectionGeneration = this.connectionGeneration;
    const submitGeneration = ++this.submitGeneration;
    this.submitAbort = abortController;

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.csrfToken()
        },
        signal: abortController.signal
      });
      const contentType = response.headers.get("content-type") || "";
      if (response.status === 401 || response.status === 403 || !contentType.includes("application/json")) {
        throw new Error("Your Pinball session expired. Sign in again to send a request.");
      }
      const payload = await response.json();
      if (!this.submitIsCurrent(connectionGeneration, submitGeneration, abortController)) return;

      if (!response.ok) {
        throw new Error(payload.error || (response.status === 401 ? "Sign in again to send a request." : "The request line did not answer."));
      }

      if (this.hasBoardTarget && payload.board_html) this.boardTarget.innerHTML = payload.board_html;
      if (this.hasTotalCountTarget && Number.isFinite(Number(payload.total_count))) {
        this.totalCountTarget.textContent = String(payload.total_count);
      }
      if (this.hasBoardTarget) {
        this.boardTarget.dispatchEvent(new CustomEvent("longplay-request-wire:refresh", { bubbles: true }));
      }

      const message = payload.message || "Request received. The radio never stopped.";
      this.setStatus(message);
      this.showToast(message);
      form.reset();
      this.stopStatusPolling();
      this.statusUrl = this.sameOriginStatusUrl(payload.request?.status_url);
      if (this.statusUrl) this.pollRequestStatus(true);
    } catch (error) {
      if (error.name === "AbortError" || !this.submitIsCurrent(connectionGeneration, submitGeneration, abortController)) return;
      this.setStatus(error.message || "Could not send that request.", true);
      this.updateProgress("failed");
    } finally {
      if (this.submitAbort === abortController) this.submitAbort = null;
      if (this.connected && this.connectionGeneration === connectionGeneration && this.submitGeneration === submitGeneration) {
        this.setSubmitting(false);
      }
    }
  }

  searchCatalog() {
    window.clearTimeout(this.catalogTimer);
    this.catalogTimer = window.setTimeout(() => this.fetchCatalog({ append: false }), 250);
  }

  loadMoreCatalog() {
    if (this.catalogCursor) this.fetchCatalog({ append: true });
  }

  async fetchCatalog({ append }) {
    if (!this.hasCatalogQueryTarget || !this.hasCatalogSelectTarget) return;

    const form = this.catalogSelectTarget.closest("form");
    const endpoint = form?.dataset.catalogUrl;
    if (!endpoint) return;

    this.catalogAbort?.abort();
    this.catalogAbort = new AbortController();
    const url = new URL(endpoint, window.location.origin);
    const query = this.catalogQueryTarget.value.trim();
    if (query) url.searchParams.set("q", query);
    if (append && this.catalogCursor) url.searchParams.set("cursor", this.catalogCursor);
    this.setCatalogStatus(append ? "Loading more tracks…" : "Searching the full Longplay catalog…");

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: this.catalogAbort.signal
      });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Catalog search did not answer.");

      this.renderCatalogResults(payload.results || [], { append });
      this.catalogCursor = payload.next_cursor || null;
      if (this.hasCatalogMoreTarget) this.catalogMoreTarget.hidden = !this.catalogCursor;
      const count = Array.isArray(payload.results) ? payload.results.length : 0;
      this.setCatalogStatus(count > 0 ? `${count} track${count === 1 ? "" : "s"} loaded${this.catalogCursor ? "; more available" : ""}.` : "No requestable tracks matched that search.");
    } catch (error) {
      if (error.name === "AbortError") return;
      this.setCatalogStatus(error.message || "Catalog search did not answer.", true);
    }
  }

  renderCatalogResults(results, { append }) {
    const select = this.catalogSelectTarget;
    const selected = select.value;
    if (!append) {
      select.replaceChildren(new Option("Choose a public track", ""));
    }
    const existing = new Set(Array.from(select.options).map((option) => option.value));
    results.forEach((track) => {
      const value = String(track.extra_id || "");
      if (!value || existing.has(value)) return;
      select.add(new Option(`${track.title || "untitled track"} — ${track.artist || "anonymous artist"}`, value));
      existing.add(value);
    });
    if (existing.has(selected)) select.value = selected;
  }

  setCatalogStatus(message, error = false) {
    if (!this.hasCatalogStatusTarget) return;
    this.catalogStatusTarget.textContent = message;
    this.catalogStatusTarget.classList.toggle("is-error", error);
  }

  async pollRequestStatus(immediate = false) {
    window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
    if (!this.connected || !this.statusUrl) return;
    const statusUrl = this.statusUrl;
    const statusGeneration = this.statusGeneration;
    const connectionGeneration = this.connectionGeneration;
    if (!immediate) {
      this.pollTimer = window.setTimeout(() => {
        this.pollTimer = null;
        if (!this.statusIsCurrent(connectionGeneration, statusGeneration, statusUrl)) return;
        this.pollRequestStatus(true);
      }, 3000);
      return;
    }

    this.statusAbort?.abort();
    const abortController = new AbortController();
    this.statusAbort = abortController;
    try {
      const response = await fetch(statusUrl, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: abortController.signal
      });
      if (!this.statusIsCurrent(connectionGeneration, statusGeneration, statusUrl, abortController)) return;
      const contentType = response.headers.get("content-type") || "";
      if (response.status === 401 || response.status === 403 || !contentType.includes("application/json")) {
        this.setStatus("Your Pinball session expired. Sign in again to follow this request.", true);
        this.stopStatusPolling();
        return;
      }
      const payload = await response.json();
      if (!this.statusIsCurrent(connectionGeneration, statusGeneration, statusUrl, abortController)) return;
      if (response.status === 404 || response.status === 410) {
        this.setStatus(payload.error || "This request is no longer available.", true);
        this.stopStatusPolling();
        return;
      }
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Request status did not answer.");

      this.updateProgress(payload.state);
      let message = payload.message || "Priority request is moving through the station.";
      if (payload.state === "ready" && payload.activates_at_ms) {
        const seconds = Math.max(0, Math.ceil((Number(payload.activates_at_ms) - Number(payload.server_now_ms || Date.now())) / 1000));
        message = `${message} Boundary in about ${seconds} seconds.`;
      }
      this.setStatus(message, payload.state === "failed" || payload.state === "cancelled");
      if (payload.terminal) {
        this.showToast(message);
        this.stopStatusPolling();
      } else {
        this.pollRequestStatus(false);
      }
    } catch (error) {
      if (error.name === "AbortError" || !this.statusIsCurrent(connectionGeneration, statusGeneration, statusUrl, abortController)) return;
      this.setStatus(error.message || "Request status did not answer; the durable station worker will keep trying.", true);
      this.pollRequestStatus(false);
    } finally {
      if (this.statusAbort === abortController) this.statusAbort = null;
    }
  }

  submitIsCurrent(connectionGeneration, submitGeneration, abortController) {
    return this.connected &&
      this.connectionGeneration === connectionGeneration &&
      this.submitGeneration === submitGeneration &&
      !abortController.signal.aborted;
  }

  statusIsCurrent(connectionGeneration, statusGeneration, statusUrl, abortController = null) {
    return this.connected &&
      this.connectionGeneration === connectionGeneration &&
      this.statusGeneration === statusGeneration &&
      this.statusUrl === statusUrl &&
      !abortController?.signal.aborted;
  }

  stopStatusPolling() {
    window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.statusGeneration += 1;
    this.statusAbort?.abort();
    this.statusAbort = null;
    this.statusUrl = null;
  }

  sameOriginStatusUrl(value) {
    if (!value) return null;

    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}`;
    } catch (_error) {
      return null;
    }
  }

  updateProgress(state) {
    if (!this.hasProgressTarget) return;
    const order = ["submitted", "copy", "render", "ready", "played"];
    const index = order.indexOf(state);
    const terminalError = state === "failed" || state === "cancelled";
    this.progressTarget.querySelectorAll("[data-hot-request-stage]").forEach((item) => {
      const itemIndex = order.indexOf(item.dataset.hotRequestStage);
      item.classList.toggle("is-current", !terminalError && itemIndex === Math.max(index, 0));
      item.classList.toggle("is-complete", !terminalError && index >= 0 && itemIndex < index);
      item.classList.toggle("is-error", terminalError);
      if (item.classList.contains("is-current")) item.setAttribute("aria-current", "step");
      else item.removeAttribute("aria-current");
    });
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  setSubmitting(value) {
    this.submitting = value;
    if (!this.hasSubmitTarget) return;

    this.submitTarget.dataset.idleLabel ||= this.submitTarget.value;
    this.submitTarget.disabled = value;
    this.submitTarget.setAttribute("aria-busy", String(value));
    this.submitTarget.value = value ? "SENDING…" : this.submitTarget.dataset.idleLabel;
  }

  setStatus(message, error = false) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("is-error", error);
  }

  resetStatus() {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = "";
    this.statusTarget.classList.remove("is-error");
    this.updateProgress("submitted");
  }

  showToast(message) {
    if (!this.hasToastTarget) return;

    window.clearTimeout(this.toastTimer);
    this.toastTarget.textContent = message;
    this.toastTarget.hidden = false;
    this.toastTimer = window.SiteNotices.show(this.toastTarget);
  }

  clearTimers() {
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.pollTimer);
    window.clearTimeout(this.catalogTimer);
    this.submitAbort?.abort();
    this.statusAbort?.abort();
    this.catalogAbort?.abort();
    this.submitAbort = null;
    this.statusAbort = null;
    this.catalogAbort = null;
  }
}
